import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from './cn';

/** Standard page gutter and max width. Every surface sits inside one of these. */
export const Container = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('container-editorial', className)} {...props} />
);

export type SectionTone = 'default' | 'alt' | 'blue' | 'gold' | 'ink';

export interface SectionProps extends HTMLAttributes<HTMLElement> {
  /**
   * The band's surface. Alternating `default` and `alt` alone made every section
   * look the same, so `blue` and `gold` give a page real colour rhythm and `ink`
   * gives it one high-contrast moment — typically the closing call to action.
   */
  tone?: SectionTone;
  /** Set false when the section manages its own container. */
  contained?: boolean;
}

// `ink` flips the type colours with it, so a section can be inverted by changing
// one prop rather than by restating every text class inside it.
const TONES: Record<SectionTone, string> = {
  default: 'bg-background-main',
  alt: 'bg-background-alt',
  blue: 'bg-surface-blue',
  gold: 'bg-surface-gold',
  ink: 'bg-surface-ink text-white [&_h1]:text-white [&_h2]:text-white [&_h3]:text-white [&_.eyebrow]:text-gold',
};

export const Section = ({ tone = 'default', contained = true, className, children, ...props }: SectionProps) => (
  <section className={cn('section-y', TONES[tone], className)} {...props}>
    {contained ? <Container>{children}</Container> : children}
  </section>
);

export interface SectionHeadingProps {
  eyebrow?: string;
  /** Small colour swatch before the eyebrow — ties a band to its category. */
  eyebrowAccent?: string;
  /** For sections on the `ink` tone. */
  inverted?: boolean;
  title: ReactNode;
  description?: ReactNode;
  /** Centered suits landing bands; left suits dense listing pages. */
  align?: 'left' | 'center';
  action?: ReactNode;
  className?: string;
}

// Consistent eyebrow → title → description rhythm, so every section on the site
// introduces itself the same way.
export const SectionHeading = ({
  eyebrow,
  eyebrowAccent,
  inverted = false,
  title,
  description,
  align = 'left',
  action,
  className,
}: SectionHeadingProps) => (
  <div
    className={cn(
      'mb-10 sm:mb-14',
      align === 'center' ? 'text-center max-w-2xl mx-auto' : 'sm:flex sm:items-end sm:justify-between sm:gap-8',
      className,
    )}
  >
    <div className={align === 'center' ? undefined : 'max-w-2xl'}>
      {eyebrow && (
        <p className={cn('eyebrow mb-3 flex items-center gap-2', align === 'center' && 'justify-center')}>
          {eyebrowAccent && <span aria-hidden="true" className={cn('h-2.5 w-2.5 rounded-sm', eyebrowAccent)} />}
          {eyebrow}
        </p>
      )}
      <h2 className={cn('text-h2 sm:text-h1', inverted ? 'text-white' : 'text-text-primary')}>{title}</h2>
      {description && (
        <p className={cn('mt-4 text-body-lg', inverted ? 'text-white/70' : 'text-text-secondary')}>{description}</p>
      )}
    </div>
    {action && <div className={cn('shrink-0', align === 'center' ? 'mt-6' : 'mt-6 sm:mt-0')}>{action}</div>}
  </div>
);

export default Section;
